import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  DateTimeField,
  EmptyState,
  Field,
  Icon,
  Modal,
  ModalActions,
  SearchSelect,
  TextArea,
  Toast,
  showToast,
  RouteMap,
} from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  houseLocalOf,
  instantFromHouseLocal,
  parseMoneyInput,
  toColumn,
  applyInputMask,
  memberTrackingMessage,
} from '@evogard/domain';
import type { DispatchDetail } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useRealtimeConnected } from '../../lib/realtime-bus.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import { ProgressPanel } from './ProgressPanel.js';
import {
  AccountBlock,
  DeskSummary,
  OverviewTab,
  PhotosTab,
  Timeline,
  centralTimeline,
  providerTimeline,
} from './DispatchDeskTabs.js';
import './live.js';

/** As abas da mesa (30/10/2026). */
type DeskTab = 'resumo' | 'prestador' | 'rota' | 'fotos' | 'script' | 'central';
import { ASSISTANCE_KEY, decodePolyline, when } from './assistance.js';

/** Copia o texto e registra que foi COPIADO — o sistema não sabe se foi enviado. */
export function useCopyScript(id: number) {
  const record = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.scriptCopied({ params: { id }, body: {} }),
        204,
        'Não foi possível registrar a cópia.',
      ),
  });
  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      record.mutate();
      showToast((dismiss) => (
        <Toast title="Script copiado" icon="copiar" onClose={dismiss}>
          <p className="text-sm">Cole no WhatsApp do prestador.</p>
        </Toast>
      ));
    } catch {
      showToast((dismiss) => (
        <Toast title="Não foi possível copiar" icon="atencao" onClose={dismiss}>
          <p className="text-sm">Selecione o texto do script e copie à mão.</p>
        </Toast>
      ));
    }
  };
}

/**
 * Copia a mensagem do associado, com o link dele (30/10/2026). Não registra
 * no histórico: o script do prestador conta porque é o acionamento saindo; a
 * mensagem do associado é cortesia, e o sistema também não sabe se foi enviada.
 */
async function copyMemberLink(code: string, url: string) {
  try {
    await navigator.clipboard.writeText(memberTrackingMessage(code, url));
    showToast((dismiss) => (
      <Toast title="Link do associado copiado" icon="copiar" onClose={dismiss}>
        <p className="text-sm">Cole no WhatsApp do associado.</p>
      </Toast>
    ));
  } catch {
    showToast((dismiss) => (
      <Toast title="Não foi possível copiar" icon="atencao" onClose={dismiss}>
        <p className="text-sm">{url}</p>
      </Toast>
    ));
  }
}

/**
 * A janela do acionamento — da lista e da agenda.
 *
 * Mostra o que foi congelado (a conta, a rota, o script), as fotos que o
 * prestador mandou e o histórico. Os atos dependem da situação: o agendado
 * executa, remarca e cancela; o acionado reenvia o link e cancela; o pago só
 * se lê.
 */
export function DispatchDesk({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const canCreate = can(context, 'assistance.dispatches', 'criar');
  const canExecute = can(context, 'assistance.dispatches', 'executar');
  const canCancel = can(context, 'assistance.dispatches', 'cancelar');
  const [mode, setMode] = useState<'ver' | 'cancelar' | 'remarcar'>('ver');
  const [tab, setTab] = useState<DeskTab | null>(null);
  const live = useRealtimeConnected();

  const query = useQuery({
    queryKey: [...ASSISTANCE_KEY, 'acionamento', id],
    /* O andamento chega pelo tempo real; a releitura é a reserva sem socket. */
    refetchInterval: live ? 120_000 : 20_000,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.getDispatch({ params: { id } }),
        200,
        'Não foi possível abrir o acionamento.',
      ),
  });
  const copy = useCopyScript(id);
  const refresh = (d: DispatchDetail) => {
    client.setQueryData([...ASSISTANCE_KEY, 'acionamento', id], d);
    void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
  };
  /*
   * O agendado reconfere a cota ao executar (01/11/2026): uma emergência pode
   * ter usado ela no meio do caminho. A recusa abre o motivo, em vez de ser só
   * uma mensagem de erro sem saída.
   */
  const canRelease = can(context, 'assistance.dispatches', 'liberar_cota');
  const [usageRefusal, setUsageRefusal] = useState<string | null>(null);
  const [usageReason, setUsageReason] = useState('');
  const execute = useMutation({
    mutationFn: async () => {
      const r = await api.assistanceDispatches.executeDispatch({
        params: { id },
        body: usageRefusal ? { usageReleaseReason: usageReason.trim() } : {},
      });
      if (r.status === 400 && (r.body as { code?: string }).code === 'cota_excedida') {
        setUsageRefusal((r.body as { message: string }).message);
        return null;
      }
      return ok(r, 200, 'Não foi possível executar.');
    },
    onSuccess: (d) => {
      if (!d) return;
      setUsageRefusal(null);
      setUsageReason('');
      refresh(d);
    },
  });
  const resend = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.resendLink({ params: { id }, body: {} }),
        200,
        'Não foi possível trocar o link.',
      ),
    onSuccess: refresh,
  });
  const openFile = useMutation({
    mutationFn: async (fileId: number) =>
      ok(
        await api.assistanceDispatches.dispatchFileUrl({ params: { id, fileId } }),
        200,
        'Não foi possível abrir a foto.',
      ),
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener'),
  });

  const d = query.data;
  const service = d
    ? ASSISTANCE_SERVICES.find((s) => s.type === d.serviceType)
    : undefined;
  /* Abre no andamento quando há um: é o que a central quer saber de um acionado. */
  const current: DeskTab = tab ?? (d?.progress ? 'prestador' : 'resumo');
  const attention = Boolean(
    d?.progress && (d.progress.late || d.progress.signalLost || d.progress.plateLocked),
  );
  const central = d ? centralTimeline(d) : [];

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      size="xl"
      tone="destaque"
      icon="assistencia"
      title={
        d ? `${service?.label ?? ''} · ${d.memberName ?? 'Associado'}` : 'Acionamento'
      }
      descricao={
        d
          ? `${d.code} · protocolo OPA ${d.opaSuiteProtocol} · aberto por ${d.createdByName ?? '—'} em ${when(d.createdAt)}`
          : undefined
      }
    >
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {(execute.error || resend.error || openFile.error) && (
        <Alert tone="erro">
          {(execute.error ?? resend.error ?? openFile.error)!.message}
        </Alert>
      )}
      {usageRefusal && (
        <div className="flex flex-col gap-2">
          <Alert tone="erro" title="Passou da cota">
            {usageRefusal}
          </Alert>
          {canRelease && (
            <>
              <Field
                label="Motivo da liberação"
                required
                maxLength={500}
                value={usageReason}
                onChange={(e) => setUsageReason(e.target.value)}
                help="Fica gravado no acionamento, com o seu nome e a hora."
              />
              <div>
                <Button
                  size="sm"
                  disabled={usageReason.trim().length < 3}
                  loading={execute.isPending}
                  onClick={() => execute.mutate()}
                >
                  Liberar e executar
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {d && mode === 'cancelar' && (
        <CancelForm
          d={d}
          onDone={(r) => {
            if (r) refresh(r);
            setMode('ver');
          }}
        />
      )}
      {d && mode === 'remarcar' && (
        <RescheduleForm
          d={d}
          onDone={(r) => {
            if (r) refresh(r);
            setMode('ver');
          }}
        />
      )}
      {d && mode === 'ver' && (
        <div className="flex flex-col gap-4">
          <DeskSummary d={d} />

          <div className="flex flex-wrap items-center gap-2">
            {d.scheduledFor && !d.executedAt && (
              <Badge tone="progresso">Marcado para {when(d.scheduledFor)}</Badge>
            )}
            {d.executedAt && (
              <Badge tone="neutro">Acionado em {when(d.executedAt)}</Badge>
            )}
            {d.settlement && (
              <Badge tone="sucesso">
                Pago ·{' '}
                {d.settlement.kind === 'a_vista'
                  ? 'à vista'
                  : `fechamento ${d.settlement.competence?.slice(5, 7)}/${d.settlement.competence?.slice(0, 4)}`}
              </Badge>
            )}
            <span className="ml-auto flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secundario"
                icon="copiar"
                onClick={() => void copy(d.serviceScript)}
              >
                Copiar script
              </Button>
              {d.memberLinkUrl && (
                <Button
                  size="sm"
                  variant="secundario"
                  icon="copiar"
                  onClick={() => void copyMemberLink(d.code, d.memberLinkUrl!)}
                >
                  Copiar link do associado
                </Button>
              )}
              {!d.executedAt && !d.canceledAt && canExecute && (
                <Button
                  size="sm"
                  loading={execute.isPending}
                  onClick={() => execute.mutate()}
                >
                  Executar agora
                </Button>
              )}
              {!d.executedAt && !d.canceledAt && canCreate && (
                <Button
                  size="sm"
                  variant="secundario"
                  onClick={() => setMode('remarcar')}
                >
                  Remarcar ou corrigir
                </Button>
              )}
              {d.executedAt && !d.canceledAt && canCreate && (
                <Button
                  size="sm"
                  variant="secundario"
                  loading={resend.isPending}
                  onClick={() => resend.mutate()}
                >
                  Trocar o link do prestador
                </Button>
              )}
              {!d.canceledAt && !d.settlement && canCancel && (
                <Button size="sm" variant="discreto" onClick={() => setMode('cancelar')}>
                  Cancelar
                </Button>
              )}
            </span>
          </div>

          <Tabs value={current} onValueChange={(v) => setTab(v as DeskTab)}>
            <TabsList>
              <TabsTrigger value="resumo">Resumo</TabsTrigger>
              {d.progress && (
                <TabsTrigger value="prestador">
                  Andamento do prestador
                  {attention && (
                    <span
                      aria-label="pede atenção"
                      className="ml-1.5 inline-block size-2 rounded-full bg-erro"
                    />
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="rota">Rota e valor</TabsTrigger>
              <TabsTrigger value="fotos">
                Fotos{d.fileList.length ? ` (${d.fileList.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="script">Script</TabsTrigger>
              <TabsTrigger value="central">Histórico da central</TabsTrigger>
            </TabsList>

            <TabsContent value="resumo" className="mt-4">
              <OverviewTab d={d} />
            </TabsContent>

            {d.progress && (
              <TabsContent value="prestador" className="mt-4 flex flex-col gap-4">
                <ProgressPanel d={d} onChanged={refresh} />
                <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <section className="flex flex-col gap-3">
                    <h3 className="flex items-center gap-2 text-sm font-bold">
                      <Icon nome="historico" size={16} />O que o prestador fez
                    </h3>
                    <Timeline
                      label="Linha do tempo do prestador"
                      items={providerTimeline(d)}
                    />
                  </section>
                  <DeskMap d={d} />
                </div>
              </TabsContent>
            )}

            <TabsContent value="rota" className="mt-4 flex flex-col gap-4">
              <DeskMap d={d} />
              <AccountBlock d={d} />
            </TabsContent>

            <TabsContent value="fotos" className="mt-4">
              <PhotosTab
                d={d}
                opening={openFile.isPending}
                onOpen={(fileId) => openFile.mutate(fileId)}
              />
            </TabsContent>

            <TabsContent value="script" className="mt-4 flex flex-col gap-2">
              <p className="text-sm text-texto-suave">
                O texto que vai para o WhatsApp do prestador, como foi gerado.
              </p>
              <pre className="max-h-96 overflow-auto rounded-lg border border-borda bg-superficie-suave p-3 font-mono text-xs whitespace-pre-wrap">
                {d.serviceScript}
              </pre>
            </TabsContent>

            <TabsContent value="central" className="mt-4">
              {central.length ? (
                <Timeline label="Linha do tempo da central" items={central} />
              ) : (
                <EmptyState icon="historico" title="Nada registrado">
                  A central ainda não fez nada com este acionamento.
                </EmptyState>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Modal>
  );
}

/**
 * O mapa do acionamento: a rota do Google, os pinos, e o caminho que o
 * prestador fez de verdade pelo link, com o pino dele pulsando em rota.
 */
function DeskMap({ d }: { d: DispatchDetail }) {
  return (
    <RouteMap
      path={d.routePolyline ? decodePolyline(d.routePolyline) : null}
      label={`Rota de ${d.provider.name}`}
      track={d.progress?.track.map((p): [number, number] => [p.lat, p.lng])}
      pins={[
        ...(d.progress?.lastPosition && d.progress.progress !== 'finalizado'
          ? [
              {
                lat: d.progress.lastPosition.lat,
                lng: d.progress.lastPosition.lng,
                kind: 'provider' as const,
                label: `${d.provider.name} · ${when(d.progress.lastPosition.at)}`,
                /* Pulsa em rota com sinal; sem sinal, o pino para — é a última posição, não a atual. */
                live:
                  ['a_caminho', 'em_transito'].includes(d.progress.progress) &&
                  !d.progress.signalLost &&
                  Date.now() - Date.parse(d.progress.lastPosition.at) < 5 * 60_000,
              },
            ]
          : []),
        ...(d.provider.depot
          ? [
              {
                ...d.provider.depot,
                kind: 'depot' as const,
                label: `Base ${d.provider.name}: saída e retorno`,
              },
            ]
          : []),
        {
          lat: d.origin.lat,
          lng: d.origin.lng,
          kind: 'vehicle' as const,
          label: 'Veículo',
        },
        ...(d.destination
          ? [
              {
                lat: d.destination.lat,
                lng: d.destination.lng,
                kind: 'destination' as const,
                label: 'Destino',
              },
            ]
          : []),
      ]}
    />
  );
}

function CancelForm({
  d,
  onDone,
}: {
  d: DispatchDetail;
  onDone: (r?: DispatchDetail) => void;
}) {
  const [left, setLeft] = useState(d.executedAt ? 'sim' : 'nao');
  const [amount, setAmount] = useState(
    d.price?.flatAmount ? applyInputMask('money', d.price.flatAmount) : '',
  );
  const [reason, setReason] = useState('');
  const parsed = parseMoneyInput(amount);
  const cancel = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.cancelDispatch({
          params: { id: d.id },
          body: {
            reason: reason.trim(),
            providerLeft: left === 'sim',
            amount: left === 'sim' && parsed !== null ? toColumn(parsed) : null,
          },
        }),
        200,
        'Não foi possível cancelar.',
      ),
    onSuccess: (r) => onDone(r),
  });
  const valid = reason.trim().length >= 3 && (left === 'nao' || parsed !== null);
  return (
    <div className="flex flex-col gap-3">
      {cancel.error && <Alert tone="erro">{cancel.error.message}</Alert>}
      <p className="text-sm text-texto-suave">
        O acionamento fica na lista como cancelado. Nada é apagado.
      </p>
      {d.executedAt ? (
        <SearchSelect
          label="O prestador já tinha saído?"
          required
          options={[
            { value: 'sim', label: 'Sim — ele recebe pelo que fez' },
            { value: 'nao', label: 'Não — nada é pago' },
          ]}
          value={left}
          onChange={setLeft}
        />
      ) : (
        <p className="text-sm">
          É um agendamento: o prestador ainda não saiu, e nada é pago.
        </p>
      )}
      {left === 'sim' && d.executedAt && (
        <Field
          label="Valor pago ao prestador"
          required
          mask="money"
          prefix="R$"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          help="Sugestão: a saída da tabela usada no acionamento. Pode ser trocado pelo valor combinado. Entra no pagamento como os outros."
        />
      )}
      <TextArea
        label="Motivo"
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <ModalActions>
        <Button variant="discreto" onClick={() => onDone()}>
          Voltar
        </Button>
        <Button
          disabled={!valid || cancel.isPending}
          loading={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          Cancelar acionamento
        </Button>
      </ModalActions>
    </div>
  );
}

function RescheduleForm({
  d,
  onDone,
}: {
  d: DispatchDetail;
  onDone: (r?: DispatchDetail) => void;
}) {
  const [at, setAt] = useState(
    d.scheduledFor ? houseLocalOf(d.scheduledFor).slice(0, 16) : '',
  );
  const [notes, setNotes] = useState(d.providerNotes ?? '');
  const instant = instantFromHouseLocal(at);
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.updateScheduled({
          params: { id: d.id },
          body: {
            scheduledFor: instant!.toISOString(),
            providerNotes: notes.trim() || null,
          },
        }),
        200,
        'Não foi possível salvar.',
      ),
    onSuccess: (r) => onDone(r),
  });
  return (
    <div className="flex flex-col gap-3">
      {save.error && <Alert tone="erro">{save.error.message}</Alert>}
      <p className="text-sm text-texto-suave">
        Nada é enviado ao salvar: copie o script de novo e avise o prestador da nova data.
      </p>
      <DateTimeField label="Data e hora" required value={at} onChange={setAt} />
      <TextArea
        label="Observação para o prestador"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <ModalActions>
        <Button variant="discreto" onClick={() => onDone()}>
          Voltar
        </Button>
        <Button
          disabled={!instant || save.isPending}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar agendamento
        </Button>
      </ModalActions>
    </div>
  );
}
